import { cloneDeep } from 'lodash';

import {
  normalizeGuidWeightMap,
  normalizeOverrideMapValue,
  overrideMapControlledInputValue,
  overrideMapInputValue,
  setGuidWeightMapEntry,
  standardWeightDisplayNumber,
  standardWeightPlaceholderString,
} from 'js/components/RubricWeights/rubricWeightSchemeUtils';
import { standardCollator } from 'js/utils/string';

export {
  normalizeGuidWeightMap,
  normalizeOverrideMapValue,
  overrideMapControlledInputValue,
  overrideMapInputValue,
  setGuidWeightMapEntry,
  standardWeightDisplayNumber,
  standardWeightPlaceholderString,
};

function normalizeText(value) {
  return String(value ?? '').trim();
}

function normalizeWeight(value) {
  if (value === '' || value == null) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** @returns {Record<string, { name?: string, weights?: object }>} */
export function getWeightOverridesMap(document) {
  return { ...document?.weight_overrides };
}

/**
 * Enumerates `weight_overrides` map entries sorted by display name (case-insensitive).
 * Each row includes stable map key `id` plus `name` and `weights`.
 */
export function weightOverridesSortedEntries(document) {
  const m = getWeightOverridesMap(document);
  return Object.entries(m)
    .map(([id, entry]) => ({
      id,
      name: normalizeText(entry?.name),
      weights:
        entry?.weights &&
        typeof entry.weights === 'object' &&
        !Array.isArray(entry.weights)
          ? entry.weights
          : {},
    }))
    .filter((row) => row.name.length > 0)
    .sort((a, b) => standardCollator.compare(a.name || a.id, b.name || b.id));
}

/** Stable id for a new `weight_overrides` map entry (map key). */
export function newWeightSchemeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ws_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

/**
 * Normalized shape for comparing weight-related document slices (dirty checks).
 */
export function normalizeWeightSectionForCompare(document) {
  if (!document || typeof document !== 'object') {
    return { question_sets: [], weight_overrides: [] };
  }

  const questionSets = Array.isArray(document.question_sets)
    ? document.question_sets
    : [];

  const normalizedSets = questionSets.map((qs) => ({
    guid: normalizeText(qs?.guid),
    weight: normalizeWeight(qs?.weight),
    questions: Array.isArray(qs?.questions)
      ? qs.questions.map((q) => ({
          guid: normalizeText(q?.guid),
          weight: normalizeWeight(q?.weight),
        }))
      : [],
  }));

  const weightOverrides = weightOverridesSortedEntries(document).map((row) => ({
    id: normalizeText(row.id),
    name: normalizeText(row.name),
    weights: normalizeGuidWeightMap(row.weights),
  }));

  return {
    question_sets: normalizedSets,
    weight_overrides: weightOverrides,
  };
}

/**
 * @param {string} raw
 * @returns {number}
 */
export function parseStandardWeightBlur(raw) {
  const parsed = parseFloat(String(raw ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 1;
}

export function cloneDocumentWithGuids(ensureRubricDocumentGuids, document) {
  return ensureRubricDocumentGuids(cloneDeep(document ?? {}));
}

export function findQuestionSetByGuid(document, questionSetGuid) {
  return (document?.question_sets ?? []).find(
    (qs) => qs.guid === questionSetGuid
  );
}

export function findQuestionByGuids(document, questionSetGuid, questionGuid) {
  const qs = findQuestionSetByGuid(document, questionSetGuid);
  return (qs?.questions ?? []).find((q) => q.guid === questionGuid);
}

export function setQuestionSetWeight(doc, questionSetGuid, weight) {
  const sets = doc?.question_sets;
  if (!Array.isArray(sets)) return doc;
  const idx = sets.findIndex((qs) => qs.guid === questionSetGuid);
  if (idx === -1) return doc;
  const nextWeight = parseStandardWeightBlur(weight);
  const next = [...sets];
  next[idx] = { ...next[idx], weight: nextWeight };
  return { ...doc, question_sets: next };
}

export function setQuestionWeight(doc, questionSetGuid, questionGuid, weight) {
  const sets = doc?.question_sets;
  if (!Array.isArray(sets)) return doc;
  const sidx = sets.findIndex((qs) => qs.guid === questionSetGuid);
  if (sidx === -1) return doc;
  const qs = sets[sidx];
  const questions = Array.isArray(qs.questions) ? qs.questions : [];
  const qidx = questions.findIndex((q) => q.guid === questionGuid);
  if (qidx === -1) return doc;
  const nextWeight = parseStandardWeightBlur(weight);
  const nextQuestions = [...questions];
  nextQuestions[qidx] = { ...nextQuestions[qidx], weight: nextWeight };
  const nextSets = [...sets];
  nextSets[sidx] = { ...qs, questions: nextQuestions };
  return { ...doc, question_sets: nextSets };
}

export function setOverrideName(doc, schemeId, name) {
  const m = getWeightOverridesMap(doc);
  if (!Object.prototype.hasOwnProperty.call(m, schemeId)) return doc;
  const prev =
    m[schemeId] && typeof m[schemeId] === 'object' ? m[schemeId] : {};
  return {
    ...doc,
    weight_overrides: {
      ...m,
      [schemeId]: { ...prev, name },
    },
  };
}

export function setOverrideWeightEntry(doc, schemeId, nodeGuid, weight) {
  const m = getWeightOverridesMap(doc);
  if (!Object.prototype.hasOwnProperty.call(m, schemeId)) return doc;
  const entry = {
    ...(m[schemeId] && typeof m[schemeId] === 'object' ? m[schemeId] : {}),
  };
  const weights = {
    ...(entry.weights &&
    typeof entry.weights === 'object' &&
    !Array.isArray(entry.weights)
      ? entry.weights
      : {}),
  };
  const useInherit =
    weight == null || (typeof weight === 'number' && !Number.isFinite(weight));
  if (useInherit) {
    delete weights[nodeGuid];
  } else {
    weights[nodeGuid] = weight;
  }
  entry.weights = weights;
  return {
    ...doc,
    weight_overrides: { ...m, [schemeId]: entry },
  };
}

/**
 * Coerces standard weights to finite numbers (default 1) before persisting.
 */
export function sanitizeStandardWeightsOnDocument(doc) {
  const sets = Array.isArray(doc?.question_sets)
    ? doc.question_sets.map((qs) => ({
        ...qs,
        weight: parseStandardWeightBlur(qs.weight),
        questions: Array.isArray(qs.questions)
          ? qs.questions.map((q) => ({
              ...q,
              weight: parseStandardWeightBlur(q.weight),
            }))
          : qs.questions,
      }))
    : [];
  return { ...doc, question_sets: sets };
}

/**
 * Case-insensitive uniqueness on trimmed names. `exceptSchemeId` skips an entry (rename).
 */
export function schemeNameTaken(overridesMap, name, exceptSchemeId = null) {
  const t = String(name ?? '').trim();
  if (!t) return false;
  const m = getWeightOverridesMap({ weight_overrides: overridesMap });
  return Object.entries(m).some(([id, o]) => {
    if (exceptSchemeId != null && id === exceptSchemeId) return false;
    const on = String(o?.name ?? '').trim();
    if (!on) return false;
    return standardCollator.compare(on, t) === 0;
  });
}

export function addWeightOverride(
  doc,
  schemeName,
  schemeId = newWeightSchemeId()
) {
  const m = getWeightOverridesMap(doc);
  const id = String(schemeId ?? '').trim() || newWeightSchemeId();
  return {
    ...doc,
    weight_overrides: {
      ...m,
      [id]: {
        name: String(schemeName ?? '').trim(),
        weights: {},
      },
    },
  };
}

export function removeWeightOverride(doc, schemeId) {
  const m = getWeightOverridesMap(doc);
  if (!Object.prototype.hasOwnProperty.call(m, schemeId)) return doc;
  const next = { ...m };
  delete next[schemeId];
  return { ...doc, weight_overrides: next };
}
